import { gsap } from 'gsap';

export default class ScrollIndicator {
    constructor() {
        this.scrollIndicator = document.querySelector('.scroll-indicator');
        this.contactSection = document.querySelector('#contact');
        this.isScrolling = false;
        this.scrollTimeout = null;
        this.hideDelay = 100; // ms delay before hiding during scroll
        this.showDelay = 600; // ms delay before showing after scroll stops
        this.timeline = null;

        if (!this.scrollIndicator) {
            console.warn('Scroll indicator element not found');
            return;
        }

        this.init();
    }

    init() {
        // Set initial state
        gsap.set(this.scrollIndicator, {
            y: 0,
            opacity: 1
        });

        this.setupScrollListener();
    }

    setupScrollListener() {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.onScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        // Listen to both scroll and touchmove for mobile support
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('touchmove', handleScroll, { passive: true });

        // Also listen for wheel events for better desktop responsiveness
        window.addEventListener('wheel', handleScroll, { passive: true });
    }

    onScroll() {
        // Check if we're in the contact section and hide permanently if so
        if (this.isInContactSection()) {
            this.hide();
            return;
        }

        // Clear any existing timeout
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }

        // If not currently scrolling, start the scroll state
        if (!this.isScrolling) {
            this.isScrolling = true;
            // Hide the indicator with a slight delay to avoid flickering on small scrolls
            setTimeout(() => {
                if (this.isScrolling) {
                    this.hide();
                }
            }, this.hideDelay);
        }

        // Set timeout to detect when scrolling stops
        this.scrollTimeout = setTimeout(() => {
            this.isScrolling = false;
            this.show();
        }, this.showDelay);
    }

    isInContactSection() {
        if (!this.contactSection) return false;
        
        const rect = this.contactSection.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Hide indicator when contact section is significantly visible
        return rect.top <= windowHeight * 0.3;
    }

    hide() {
        if (this.scrollIndicator && !this.timeline?.isActive()) {
            this.timeline = gsap.to(this.scrollIndicator, {
                y: 60,
                opacity: 0,
                duration: 0.4,
                ease: "power2.out"
            });
        }
    }

    show() {
        if (this.scrollIndicator && !this.timeline?.isActive()) {
            this.timeline = gsap.to(this.scrollIndicator, {
                y: 0,
                opacity: 1,
                duration: 0.5,
                ease: "power2.out"
            });
        }
    }

    destroy() {
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }

        if (this.timeline) {
            this.timeline.kill();
        }

        // Remove event listeners
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('touchmove', this.handleScroll);
        window.removeEventListener('wheel', this.handleScroll);
    }
}
